import { useQuery } from '@tanstack/react-query'
import { createRoute, Link, Navigate } from '@tanstack/react-router'
import {
  ArrowDown,
  BarChart3,
  Bot,
  Calendar,
  Check,
  Clock,
  FileSignature,
  FileText,
  Flame,
  KanbanSquare,
  LayoutDashboard,
  MessageSquare,
  Palette,
  Shield,
  Users,
  Video,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { api } from '../lib/api'
import { Route as rootRoute } from './__root'

// <base href> rewrites bare "#section" links against the base URL; keep them on this page.
const WELCOME_PATH = `${import.meta.env.BASE_URL}welcome`

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/welcome',
  component: WelcomePage,
})

/* ---------------------------------------------------------------
   Hooks
   --------------------------------------------------------------- */

function useScrollReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setVisible(true)
      },
      { threshold },
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [threshold])
  return { ref, visible }
}

function AnimatedCounter({ target, suffix = '' }: { target: number; suffix?: string }) {
  const { ref, visible } = useScrollReveal()
  const [count, setCount] = useState(0)
  useEffect(() => {
    if (!visible) return
    let frame: number
    const start = performance.now()
    const duration = 2000
    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1)
      const eased = 1 - (1 - progress) ** 3
      setCount(Math.round(target * eased))
      if (progress < 1) frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [visible, target])
  return (
    <span ref={ref} className="font-mono tabular-nums">
      {count}
      {suffix}
    </span>
  )
}

function useScrolledPast(px: number) {
  const [past, setPast] = useState(false)
  useEffect(() => {
    const handler = () => setPast(window.scrollY > px)
    handler()
    window.addEventListener('scroll', handler, { passive: true })
    return () => window.removeEventListener('scroll', handler)
  }, [px])
  return past
}

/* ---------------------------------------------------------------
   Reveal wrapper
   --------------------------------------------------------------- */

function Reveal({
  children,
  className = '',
  delay = 0,
}: {
  children: React.ReactNode
  className?: string
  delay?: number
}) {
  const { ref, visible } = useScrollReveal()
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(40px)',
        transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  )
}

/* ---------------------------------------------------------------
   Device frames
   --------------------------------------------------------------- */

function BrowserFrame({
  children,
  url = 'app.ember.local',
}: {
  children: React.ReactNode
  url?: string
}) {
  return (
    <div className="relative">
      <div className="rounded-t-2xl bg-[rgb(30,30,32)] border border-b-0 border-white/[0.08] px-4 py-3 flex items-center gap-2">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-[#ff5f57]" />
          <div className="w-3 h-3 rounded-full bg-[#febc2e]" />
          <div className="w-3 h-3 rounded-full bg-[#28c840]" />
        </div>
        <div className="flex-1 ml-3 h-7 rounded-lg bg-white/[0.06] flex items-center px-3">
          <span className="text-xs text-white/30">{url}</span>
        </div>
      </div>
      <div className="rounded-b-2xl border border-t-0 border-white/[0.08] overflow-hidden bg-[#111113]">
        {children}
      </div>
      <div className="absolute -inset-8 -z-10 rounded-3xl bg-accent/[0.06] blur-3xl" />
    </div>
  )
}

function PhoneFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative mx-auto" style={{ maxWidth: 280 }}>
      <div className="rounded-[2rem] border-[3px] border-white/[0.12] bg-[#111113] overflow-hidden shadow-2xl">
        {/* Notch */}
        <div className="flex justify-center pt-2 pb-1 bg-[#111113]">
          <div className="w-20 h-5 rounded-full bg-white/[0.08]" />
        </div>
        <div className="overflow-hidden">{children}</div>
        {/* Home indicator */}
        <div className="flex justify-center py-2 bg-[#111113]">
          <div className="w-28 h-1 rounded-full bg-white/[0.15]" />
        </div>
      </div>
      <div className="absolute -inset-6 -z-10 rounded-[3rem] bg-accent/5 blur-2xl" />
    </div>
  )
}

/* ---------------------------------------------------------------
   Data — feature overview grid
   --------------------------------------------------------------- */

const FEATURE_GRID = [
  { icon: Clock, label: 'Zeiterfassung', anchor: 'zeit' },
  { icon: Calendar, label: 'Kalender', anchor: 'kalender' },
  { icon: FileText, label: 'Rechnungen', anchor: 'rechnungen' },
  { icon: Bot, label: 'KI-Assistent', anchor: 'ki' },
  { icon: LayoutDashboard, label: 'Dashboard', anchor: 'dashboard' },
  { icon: MessageSquare, label: 'Chat', anchor: 'chat' },
  { icon: Video, label: 'Meetings', anchor: 'meetings' },
  { icon: Users, label: 'Projekte & Kunden', anchor: 'projekte' },
  { icon: BarChart3, label: 'Berichte', anchor: 'berichte' },
  { icon: FileSignature, label: 'Dokumente', anchor: 'dokumente' },
  { icon: KanbanSquare, label: 'Kanban', anchor: 'kanban' },
  { icon: Palette, label: 'Personalisierung', anchor: 'settings' },
  { icon: Shield, label: 'Audit-Log', anchor: 'audit' },
]

const PRICING_FEATURES = [
  'Unbegrenzte Benutzer',
  'Alle 12+ Module inklusive',
  'KI-Assistent (42 Aktionen)',
  'PWA auf jedem Gerät',
  'Kalender-Sync (ICS)',
  'Rechnungen mit XRechnung & ZUGFeRD',
  'Chat & Jitsi Video-Meetings',
  'Digitale Dokument-Signatur',
  'Kanban-Board & Tickets',
  'Dashboard mit Drag & Drop Widgets',
  'Vollständiger Audit-Trail',
  'REST-API',
  'Deine Daten, dein Server',
  '12 Themes, 6 Schriftarten',
]

/* ---------------------------------------------------------------
   Feature section component
   --------------------------------------------------------------- */

interface FeatureItem {
  text: string
}

interface FeatureSectionProps {
  id: string
  label: string
  title: string
  description: string
  points: FeatureItem[]
  screenshots: { src: string; alt: string; type?: 'browser' | 'phone' }[]
  reverse?: boolean
}

function FeatureSection({
  id,
  label,
  title,
  description,
  points,
  screenshots,
  reverse,
}: FeatureSectionProps) {
  const textSide = (
    <div>
      <span className="text-accent text-sm font-mono uppercase tracking-wider">{label}</span>
      <h2 className="text-3xl sm:text-4xl font-bold mt-2 text-white">{title}</h2>
      <p className="text-white/60 text-lg mt-4 leading-relaxed">{description}</p>
      <ul className="mt-6 space-y-3">
        {points.map((p) => (
          <li key={p.text} className="flex items-start gap-3">
            <Check className="h-5 w-5 text-accent shrink-0 mt-0.5" strokeWidth={2.5} />
            <span className="text-white/70 text-[15px]">{p.text}</span>
          </li>
        ))}
      </ul>
    </div>
  )

  const screenshotSide = (
    <div className="space-y-6">
      {screenshots.map((s) =>
        s.type === 'phone' ? (
          <PhoneFrame key={s.src}>
            <img src={s.src} alt={s.alt} className="w-full" loading="lazy" />
          </PhoneFrame>
        ) : (
          <BrowserFrame key={s.src}>
            <img src={s.src} alt={s.alt} className="w-full" loading="lazy" />
          </BrowserFrame>
        ),
      )}
    </div>
  )

  return (
    <section id={id} className="py-20 sm:py-24 px-5 sm:px-6">
      <div className="max-w-6xl mx-auto">
        <Reveal>
          <div
            className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${reverse ? 'lg:[direction:rtl] lg:[&>*]:[direction:ltr]' : ''}`}
          >
            {reverse ? (
              <>
                {screenshotSide}
                {textSide}
              </>
            ) : (
              <>
                {textSide}
                {screenshotSide}
              </>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  )
}

/* ---------------------------------------------------------------
   Multi-screenshot feature section (for sections with 3 images)
   --------------------------------------------------------------- */

function FeatureSectionMulti({
  id,
  label,
  title,
  description,
  points,
  mainScreenshots,
  sideScreenshot,
  reverse,
}: {
  id: string
  label: string
  title: string
  description: string
  points: FeatureItem[]
  mainScreenshots: { src: string; alt: string }[]
  sideScreenshot?: { src: string; alt: string; type: 'phone' }
  reverse?: boolean
}) {
  const textSide = (
    <div>
      <span className="text-accent text-sm font-mono uppercase tracking-wider">{label}</span>
      <h2 className="text-3xl sm:text-4xl font-bold mt-2 text-white">{title}</h2>
      <p className="text-white/60 text-lg mt-4 leading-relaxed">{description}</p>
      <ul className="mt-6 space-y-3">
        {points.map((p) => (
          <li key={p.text} className="flex items-start gap-3">
            <Check className="h-5 w-5 text-accent shrink-0 mt-0.5" strokeWidth={2.5} />
            <span className="text-white/70 text-[15px]">{p.text}</span>
          </li>
        ))}
      </ul>
    </div>
  )

  const screenshotSide = (
    <div className="space-y-6">
      {mainScreenshots.map((s) => (
        <BrowserFrame key={s.src}>
          <img src={s.src} alt={s.alt} className="w-full" loading="lazy" />
        </BrowserFrame>
      ))}
      {sideScreenshot && (
        <PhoneFrame>
          <img
            src={sideScreenshot.src}
            alt={sideScreenshot.alt}
            className="w-full"
            loading="lazy"
          />
        </PhoneFrame>
      )}
    </div>
  )

  return (
    <section id={id} className="py-20 sm:py-24 px-5 sm:px-6">
      <div className="max-w-6xl mx-auto">
        <Reveal>
          <div
            className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${reverse ? 'lg:[direction:rtl] lg:[&>*]:[direction:ltr]' : ''}`}
          >
            {reverse ? (
              <>
                {screenshotSide}
                {textSide}
              </>
            ) : (
              <>
                {textSide}
                {screenshotSide}
              </>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  )
}

/* ---------------------------------------------------------------
   Live timer — counts up in real time with ember glow
   --------------------------------------------------------------- */

function LiveTimer() {
  const [elapsed, setElapsed] = useState(0)
  useEffect(() => {
    const start = Date.now()
    const tick = () => setElapsed(Date.now() - start)
    const id = setInterval(tick, 50)
    return () => clearInterval(id)
  }, [])

  const totalSeconds = Math.floor(elapsed / 1000)
  const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0')
  const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0')
  const seconds = String(totalSeconds % 60).padStart(2, '0')
  const centis = String(Math.floor((elapsed % 1000) / 10)).padStart(2, '0')

  return (
    <div className="flex items-baseline gap-1 font-mono tabular-nums">
      <span className="text-5xl sm:text-6xl md:text-7xl text-white welcome-timer-text">
        {hours}:{minutes}:{seconds}
      </span>
      <span className="text-2xl sm:text-3xl md:text-4xl text-white/40 welcome-timer-text">
        .{centis}
      </span>
    </div>
  )
}

/* ---------------------------------------------------------------
   Main page
   --------------------------------------------------------------- */

function WelcomePage() {
  const { data: meData, isLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })

  const scrolled = useScrolledPast(60)
  const [activeNav, setActiveNav] = useState('')

  // Track active section for nav highlighting — must be before early returns
  useEffect(() => {
    const sections = FEATURE_GRID.map((f) => document.getElementById(f.anchor)).filter(Boolean)
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.target.id) {
            setActiveNav(entry.target.id)
          }
        }
      },
      { threshold: 0.3 },
    )
    for (const s of sections) {
      if (s) obs.observe(s)
    }
    return () => obs.disconnect()
  }, [])

  // Suppress unused var warning
  void activeNav

  if (isLoading) return null
  if (!meData?.user) return <Navigate to="/login" />

  return (
    <div className="welcome-page min-h-screen overflow-x-hidden">
      {/* ============================================================
          NAV
          ============================================================ */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-[rgba(10,10,10,0.88)] backdrop-blur-xl border-b border-white/[0.06] shadow-[0_1px_40px_rgba(0,0,0,0.5)]'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-6xl mx-auto flex items-center justify-between px-5 sm:px-8 h-16">
          <a href={`${WELCOME_PATH}#top`} className="flex items-center gap-2.5">
            <Flame className="h-6 w-6 text-accent" strokeWidth={2.25} />
            <span className="brand-mark text-xl text-white">Ember</span>
          </a>
          <div className="hidden sm:flex items-center gap-8 text-sm text-white/50">
            <a href={`${WELCOME_PATH}#features`} className="hover:text-white transition-colors">
              Features
            </a>
            <a href={`${WELCOME_PATH}#module`} className="hover:text-white transition-colors">
              Module
            </a>
            <a href={`${WELCOME_PATH}#preise`} className="hover:text-white transition-colors">
              Preise
            </a>
          </div>
          <Link
            to="/"
            className="px-5 py-2 rounded-full text-sm font-medium bg-accent text-white hover:bg-accent-hover transition-colors shadow-[0_0_20px_rgb(var(--c-accent)/0.3)]"
          >
            Anmelden
          </Link>
        </div>
      </nav>

      {/* ============================================================
          SECTION 1 — HERO
          ============================================================ */}
      <section
        id="top"
        className="relative min-h-screen flex flex-col items-center justify-center px-5 overflow-hidden"
      >
        {/* Animated gradient mesh background */}
        <div className="absolute inset-0 welcome-hero-mesh" />

        {/* Floating ember particles */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="welcome-particle welcome-particle-1" />
          <div className="welcome-particle welcome-particle-2" />
          <div className="welcome-particle welcome-particle-3" />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center text-center max-w-5xl mx-auto pt-20">
          {/* Brand */}
          <h1 className="font-display text-7xl sm:text-8xl md:text-9xl text-white mb-6 leading-none welcome-timer-text">
            Ember
          </h1>

          {/* Live timer */}
          <LiveTimer />

          {/* Subline */}
          <p className="text-xl sm:text-2xl md:text-3xl text-white/80 font-medium mb-6 mt-6">
            Das einzige SaaS, das du brauchst.
          </p>

          {/* Description */}
          <p className="text-base sm:text-lg text-white/50 max-w-3xl mb-10 leading-relaxed">
            Zeiterfassung. Rechnungen. Kalender. Chat. E-Mail. Meetings. KI-Assistent. Dokumente.
            Kanban. Alles in einer App.
          </p>

          {/* CTA */}
          <Link
            to="/"
            className="group relative px-8 py-4 rounded-full text-lg font-medium bg-accent text-white hover:bg-accent-hover transition-all duration-300 shadow-[0_0_40px_rgb(var(--c-accent)/0.4)] hover:shadow-[0_0_60px_rgb(var(--c-accent)/0.6)] hover:scale-105"
          >
            Kostenlos starten
            <span className="absolute inset-0 rounded-full bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
          </Link>

          {/* Hero screenshot in browser frame */}
          <div className="mt-16 sm:mt-20 w-full max-w-4xl">
            <Reveal delay={200}>
              <BrowserFrame>
                <img
                  src={`${import.meta.env.BASE_URL}showcase/dashboard.png`}
                  alt="Ember Dashboard"
                  className="w-full"
                  loading="eager"
                />
              </BrowserFrame>
            </Reveal>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 welcome-scroll-indicator">
          <ArrowDown className="h-5 w-5 text-white/30" />
        </div>
      </section>

      {/* ============================================================
          SECTION 2 — FEATURE OVERVIEW GRID
          ============================================================ */}
      <section id="features" className="py-24 sm:py-32 px-5">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="text-center mb-16 sm:mb-20">
              <div className="eyebrow mb-4">Das komplette Paket</div>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl text-white">
                Alles was dein Team braucht
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
            {FEATURE_GRID.map((f, i) => (
              <Reveal key={f.label} delay={i * 50}>
                <a
                  href={`#${f.anchor}`}
                  className="group flex flex-col items-center gap-3 p-5 sm:p-6 rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-sm transition-all duration-300 hover:border-accent/30 hover:bg-white/[0.04] hover:shadow-[0_8px_40px_-12px_rgb(var(--c-accent)/0.15)] hover:-translate-y-1 text-center"
                >
                  <div className="p-2.5 rounded-xl bg-accent/10 text-accent group-hover:bg-accent/15 transition-colors">
                    <f.icon className="h-5 w-5" strokeWidth={1.75} />
                  </div>
                  <span className="text-sm font-medium text-white/80 group-hover:text-white transition-colors">
                    {f.label}
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          SECTION 3 — DETAILED FEATURE SECTIONS
          ============================================================ */}
      <div id="module">
        {/* 3a — Stoppuhr & Zeiterfassung */}
        <section id="zeit" className="py-20 sm:py-24 px-5 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <Reveal>
              <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                <div>
                  <span className="text-accent text-sm font-mono uppercase tracking-wider">
                    Zeiterfassung
                  </span>
                  <h2 className="text-3xl sm:text-4xl font-bold mt-2 text-white">
                    Ein Klick. Die Zeit läuft.
                  </h2>
                  <p className="text-white/60 text-lg mt-4 leading-relaxed">
                    Starte den Timer mit einem Klick. Weise Projekte und Tätigkeiten zu. Sieh sofort
                    was heute passiert ist. Die Stoppuhr läuft im Hintergrund — du arbeitest einfach
                    weiter.
                  </p>
                  <ul className="mt-6 space-y-3">
                    {[
                      'Live-Timer mit Sekundenanzeige',
                      'Projekt- & Tätigkeits-Zuordnung',
                      'Tagesübersicht mit Gesamtstunden',
                      'Manuelle Nachträge möglich',
                    ].map((text) => (
                      <li key={text} className="flex items-start gap-3">
                        <Check className="h-5 w-5 text-accent shrink-0 mt-0.5" strokeWidth={2.5} />
                        <span className="text-white/70 text-[15px]">{text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="space-y-4">
                  <BrowserFrame>
                    <img
                      src={`${import.meta.env.BASE_URL}showcase/timer.png`}
                      alt="Ember Timer"
                      className="w-full"
                      loading="lazy"
                    />
                  </BrowserFrame>
                  <div className="relative ml-8 sm:ml-16 -mt-12 sm:-mt-20 z-10">
                    <div className="rounded-xl overflow-hidden border border-white/[0.1] shadow-2xl bg-[#111113] max-w-xs">
                      <img
                        src={`${import.meta.env.BASE_URL}showcase/timer-running.png`}
                        alt="Laufender Timer"
                        className="w-full"
                        loading="lazy"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* 3b — Kalender */}
        <FeatureSectionMulti
          id="kalender"
          label="Kalender"
          title="Deine Zeit, visuell."
          description="Woche, Monat oder Jahr — dein Kalender zeigt alles auf einen Blick. Einträge als farbige Blöcke, Meetings, Abwesenheiten. Per Drag & Drop verschieben. Auf dem Handy die Tagesansicht mit Wisch-Geste."
          points={[
            { text: '4 Ansichten (Tag/Woche/Monat/Jahr)' },
            { text: 'Drag & Drop auf Desktop' },
            { text: 'Touch-optimiert auf Mobile' },
            { text: 'ICS-Feed für Apple/Google/Outlook' },
          ]}
          mainScreenshots={[
            {
              src: `${import.meta.env.BASE_URL}showcase/calendar-week.png`,
              alt: 'Kalender Wochenansicht',
            },
            {
              src: `${import.meta.env.BASE_URL}showcase/calendar-month.png`,
              alt: 'Kalender Monatsansicht',
            },
          ]}
          sideScreenshot={{
            src: `${import.meta.env.BASE_URL}showcase/calendar-mobile.png`,
            alt: 'Kalender Mobile',
            type: 'phone',
          }}
          reverse
        />

        {/* 3c — Rechnungen */}
        <FeatureSection
          id="rechnungen"
          label="Rechnungen"
          title="Vom Zeiteintrag zur Rechnung."
          description="Wähle Zeiteinträge aus, gruppiere nach Projekt oder Mitarbeiter, und erstelle eine professionelle Rechnung mit einem Klick. PDF-Download, XRechnung, ZUGFeRD — alles dabei."
          points={[
            { text: 'Invoice Builder Wizard' },
            { text: '5 PDF-Vorlagen' },
            { text: 'XRechnung & ZUGFeRD' },
            { text: 'Automatische Nummerierung' },
            { text: 'Status-Tracking (Entwurf → Versendet → Bezahlt)' },
          ]}
          screenshots={[
            { src: `${import.meta.env.BASE_URL}showcase/invoices.png`, alt: 'Rechnungsübersicht' },
            {
              src: `${import.meta.env.BASE_URL}showcase/invoice-detail.png`,
              alt: 'Rechnungsdetail',
            },
          ]}
        />

        {/* 3d — KI-Assistent */}
        <FeatureSection
          id="ki"
          label="Künstliche Intelligenz"
          title="Sag was du brauchst."
          description="Der eingebaute KI-Assistent versteht natürliche Sprache und führt Aktionen aus. 'Erstelle eine Rechnung an Firma X mit 3x Produkt Y' — erledigt. Funktioniert mit Anthropic Claude oder deinem eigenen lokalen KI-Modell."
          points={[
            { text: '42 verfügbare Aktionen' },
            { text: 'Natürliche Sprache (Deutsch/Englisch)' },
            { text: 'Anthropic oder eigene KI' },
            { text: 'Produktsuche im Katalog' },
          ]}
          screenshots={[
            { src: `${import.meta.env.BASE_URL}showcase/ai-assistant.png`, alt: 'KI-Assistent' },
          ]}
          reverse
        />

        {/* 3e — Dashboard */}
        <section id="dashboard" className="py-20 sm:py-24 px-5 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <Reveal>
              <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                <div>
                  <span className="text-accent text-sm font-mono uppercase tracking-wider">
                    Dashboard
                  </span>
                  <h2 className="text-3xl sm:text-4xl font-bold mt-2 text-white">Dein Cockpit.</h2>
                  <p className="text-white/60 text-lg mt-4 leading-relaxed">
                    Konfiguriere dein Dashboard mit Widgets. Soll/Ist-Vergleich, Web-Shortcuts,
                    Abwesenheitsübersicht. Drag & Drop im Bearbeitungsmodus. Auf dem Smartphone
                    automatisch gestapelt.
                  </p>
                  <ul className="mt-6 space-y-3">
                    {[
                      'Drag & Drop Widgets',
                      'Soll/Ist Wochenchart',
                      'Admin-Vorlagen',
                      'Responsiv auf allen Geräten',
                    ].map((text) => (
                      <li key={text} className="flex items-start gap-3">
                        <Check className="h-5 w-5 text-accent shrink-0 mt-0.5" strokeWidth={2.5} />
                        <span className="text-white/70 text-[15px]">{text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="space-y-6">
                  <BrowserFrame>
                    <img
                      src={`${import.meta.env.BASE_URL}showcase/dashboard.png`}
                      alt="Dashboard Desktop"
                      className="w-full"
                      loading="lazy"
                    />
                  </BrowserFrame>
                  <PhoneFrame>
                    <img
                      src={`${import.meta.env.BASE_URL}showcase/dashboard-mobile.png`}
                      alt="Dashboard Mobile"
                      className="w-full"
                      loading="lazy"
                    />
                  </PhoneFrame>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* 3f — Chat */}
        <FeatureSection
          id="chat"
          label="Kommunikation"
          title="Dein Team, direkt verbunden."
          description="1-zu-1 und Gruppen-Chats direkt in der App. Push-Benachrichtigungen auf allen Geräten. Kein Slack, kein Teams — alles an einem Ort."
          points={[
            { text: '1-zu-1 & Gruppen' },
            { text: 'Push Notifications' },
            { text: 'Ungelesen-Badges' },
            { text: 'Browser & PWA' },
          ]}
          screenshots={[{ src: `${import.meta.env.BASE_URL}showcase/chat.png`, alt: 'Chat' }]}
          reverse
        />

        {/* 3g — Meetings */}
        <FeatureSection
          id="meetings"
          label="Meetings"
          title="Meetings mit einem Klick."
          description="Plane Meetings, lade Teilnehmer ein, starte Jitsi-Videokonferenzen mit JWT-Authentifizierung. Teilnehmer werden per Push benachrichtigt bei Änderungen."
          points={[
            { text: 'Jitsi-Integration mit JWT' },
            { text: 'RSVP (Zusagen/Absagen)' },
            { text: 'Push bei Änderungen' },
            { text: 'Im Kalender sichtbar' },
          ]}
          screenshots={[
            { src: `${import.meta.env.BASE_URL}showcase/meetings.png`, alt: 'Meetings' },
          ]}
        />

        {/* 3h — Projekte & Kunden */}
        <FeatureSection
          id="projekte"
          label="Verwaltung"
          title="Alles im Griff."
          description="Kunden mit vollständiger Rechnungsadresse. Projekte mit Budgets, Stundensätzen und Farbcodes. Alles miteinander verknüpft — vom Zeiteintrag bis zur Rechnung."
          points={[
            { text: 'Kundenverwaltung mit Billing-Adressen' },
            { text: 'Projektbudgets & Stundensätze' },
            { text: 'Farbcodierung im Kalender' },
            { text: 'Archivierung & Reaktivierung' },
          ]}
          screenshots={[
            { src: `${import.meta.env.BASE_URL}showcase/projects.png`, alt: 'Projekte' },
            { src: `${import.meta.env.BASE_URL}showcase/customers.png`, alt: 'Kundenverwaltung' },
          ]}
          reverse
        />

        {/* 3i — Berichte */}
        <FeatureSection
          id="berichte"
          label="Berichte"
          title="Zahlen, die überzeugen."
          description="Zeitraum-Reports mit Projekt-Aggregation und Umsatzvorschau. CSV-Export für die Buchhaltung. PDF-Reports für Kunden. Soll/Ist-Vergleich pro Woche."
          points={[
            { text: 'Projekt-Aggregation mit Umsatz' },
            { text: 'CSV & PDF Export' },
            { text: 'Soll/Ist Wochenvergleich' },
            { text: 'Filter nach Projekt/Kunde/Mitarbeiter' },
          ]}
          screenshots={[
            { src: `${import.meta.env.BASE_URL}showcase/reports.png`, alt: 'Berichte & Reports' },
          ]}
        />

        {/* 3j — Dokumente */}
        <FeatureSection
          id="dokumente"
          label="Dokumente"
          title="Hochladen. Unterschreiben. Fertig."
          description="PDF-Dokumente hochladen, an Mitarbeiter zur Unterschrift senden. Digitale Signatur mit Datum und Ort. Audit-Trail für jedes Dokument."
          points={[
            { text: 'PDF-Upload & -Viewer' },
            { text: 'Digitale Unterschrift' },
            { text: 'Posteingang für Signaturen' },
            { text: 'Dokumenten-Audit-Trail' },
          ]}
          screenshots={[
            {
              src: `${import.meta.env.BASE_URL}showcase/documents.png`,
              alt: 'Dokumente & Signatur',
            },
          ]}
          reverse
        />

        {/* 3k — Kanban */}
        <FeatureSection
          id="kanban"
          label="Projektmanagement"
          title="Tickets rein. Board sortieren."
          description="Mitarbeiter melden Bugs und Feature-Wünsche als Tickets. Admins verwalten sie auf dem Kanban-Board — per Drag & Drop zwischen Spalten verschieben. Eigene Spalten erstellen."
          points={[
            { text: 'Bug-Reports & Feature-Requests' },
            { text: 'Kanban-Board mit Drag & Drop' },
            { text: 'Datei-Anhänge & Kommentare' },
            { text: 'Eigene Spalten & Workflows' },
          ]}
          screenshots={[
            { src: `${import.meta.env.BASE_URL}showcase/kanban.png`, alt: 'Kanban Board' },
            { src: `${import.meta.env.BASE_URL}showcase/tickets.png`, alt: 'Tickets' },
          ]}
        />

        {/* 3l — Einstellungen */}
        <FeatureSection
          id="settings"
          label="Personalisierung"
          title="Dein Ember. Dein Style."
          description="12 Themes, 6 Schriftarten, eigenes Logo. Kalender-Abo für iPhone und Android. Push-Benachrichtigungen. Als PWA auf dem Homescreen installierbar."
          points={[
            { text: '12 Farbthemen' },
            { text: '6 Schriftarten & 4 Größen' },
            { text: 'Kalender-Sync (ICS)' },
            { text: 'PWA auf jedem Gerät' },
          ]}
          screenshots={[
            {
              src: `${import.meta.env.BASE_URL}showcase/settings-design.png`,
              alt: 'Design-Einstellungen',
            },
            {
              src: `${import.meta.env.BASE_URL}showcase/settings-integrations.png`,
              alt: 'Integrationen',
            },
          ]}
          reverse
        />

        {/* 3m — Audit-Log */}
        <FeatureSection
          id="audit"
          label="Sicherheit"
          title="Jede Aktion nachvollziehbar."
          description="Wer hat wann was geändert? Das Aktivitätsprotokoll loggt jede Mutation mit Vorher/Nachher-Snapshot. Admin kann Löschungen rückgängig machen."
          points={[
            { text: 'Vollständiger Audit-Trail' },
            { text: 'Vorher/Nachher-Vergleich' },
            { text: 'Rückgängig-Funktion' },
            { text: 'IP-Tracking' },
          ]}
          screenshots={[
            { src: `${import.meta.env.BASE_URL}showcase/audit-log.png`, alt: 'Audit-Log' },
          ]}
        />
      </div>

      {/* ============================================================
          SECTION 4 — STATS
          ============================================================ */}
      <section className="py-24 sm:py-32 px-5">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {[
              { target: 12, suffix: '+', label: 'Module' },
              { target: 42, suffix: '', label: 'KI-Tools' },
              { target: 12, suffix: '', label: 'Themes' },
              { target: 100, suffix: '%', label: 'Selfhosted' },
            ].map((stat) => (
              <Reveal key={stat.label} delay={100}>
                <div className="text-center p-6 sm:p-8 rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-sm">
                  <div className="text-4xl sm:text-5xl font-display text-accent mb-2">
                    <AnimatedCounter target={stat.target} suffix={stat.suffix} />
                  </div>
                  <div className="text-sm text-white/50">{stat.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          SECTION 5 — PRICING
          ============================================================ */}
      <section id="preise" className="py-24 sm:py-32 px-5">
        <div className="max-w-2xl mx-auto">
          <Reveal>
            <div className="text-center mb-12">
              <div className="eyebrow mb-4">Preise</div>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl text-white">
                Selfhosted. Open Source. Kostenlos.
              </h2>
            </div>
          </Reveal>

          <Reveal delay={100}>
            <div className="relative p-8 sm:p-10 rounded-3xl border border-accent/20 bg-white/[0.02] backdrop-blur-sm">
              {/* Accent glow top border */}
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent to-transparent" />

              <div className="text-center mb-8">
                <div className="text-5xl sm:text-6xl font-display text-white mb-2">Kostenlos</div>
                <p className="text-white/50">Open Source. Keine versteckten Kosten.</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 mb-10">
                {PRICING_FEATURES.map((feat) => (
                  <div key={feat} className="flex items-center gap-3">
                    <Check className="h-4 w-4 text-emerald-500 shrink-0" />
                    <span className="text-sm text-white/60">{feat}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/"
                  className="inline-flex items-center justify-center px-8 py-3.5 rounded-full text-base font-medium bg-accent text-white hover:bg-accent-hover transition-all duration-300 shadow-[0_0_30px_rgb(var(--c-accent)/0.3)] hover:shadow-[0_0_50px_rgb(var(--c-accent)/0.5)] hover:scale-105"
                >
                  Jetzt einrichten
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============================================================
          SECTION 6 — FOOTER
          ============================================================ */}
      <footer className="border-t border-white/[0.04] py-12 sm:py-16 px-5">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2.5">
            <Flame className="h-5 w-5 text-accent" strokeWidth={2.25} />
            <span className="brand-mark text-lg text-white">Ember</span>
          </div>

          <div className="flex items-center gap-6 text-sm text-white/30">
            <span className="hover:text-white/50 transition-colors cursor-pointer">GitHub</span>
            <span className="hover:text-white/50 transition-colors cursor-pointer">Docs</span>
            <span className="hover:text-white/50 transition-colors cursor-pointer">Impressum</span>
          </div>

          <div className="text-sm text-white/30">
            Made with{' '}
            <span className="inline-block welcome-flame-wiggle" role="img" aria-label="Feuer">
              <Flame className="inline h-3.5 w-3.5 text-accent" />
            </span>{' '}
            by IT, Financeflow
          </div>
        </div>
      </footer>
    </div>
  )
}
